import React, { useState, useEffect, useRef } from 'react';
import {
    Card,
    Select,
    Button,
    Input,
    Space,
    Typography,
    Alert,
    Spin,
    Switch,
    DatePicker,
    Tag,
    message,
    Tooltip,
} from 'antd';
import {
    ReloadOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    DownloadOutlined,
    ClearOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetCloudWatchLogGroupsQuery,
    useGetCloudWatchLogStreamsQuery,
    useGetCloudWatchLogEventsQuery,
} from '../../../services/nestjs-base-api';
import { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface LogEntry {
    timestamp: number;
    message: string;
    ingestionTime?: number;
}

export const CloudWatchLogsTab: React.FC = () => {
    const { user } = useAuth();
    const [selectedLogGroup, setSelectedLogGroup] = useState<string | null>(null);
    const [selectedLogStream, setSelectedLogStream] = useState<string | null>(null);
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [isTailing, setIsTailing] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
    const logsEndRef = useRef<HTMLDivElement>(null);
    const eventSourceRef = useRef<EventSource | null>(null);

    // Fetch log groups
    const {
        data: logGroups,
        isLoading: logGroupsLoading,
        refetch: refetchLogGroups,
    } = useGetCloudWatchLogGroupsQuery(
        { jwtToken: user?.jwtToken || '', limit: 50 },
        { skip: !user?.jwtToken }
    );

    // Fetch log streams
    const {
        data: logStreams,
        isLoading: logStreamsLoading,
        refetch: refetchLogStreams,
    } = useGetCloudWatchLogStreamsQuery(
        {
            jwtToken: user?.jwtToken || '',
            logGroupName: selectedLogGroup || '',
            orderBy: 'LastEventTime',
            descending: true,
            limit: 50,
        },
        { skip: !user?.jwtToken || !selectedLogGroup }
    );

    // Fetch log events
    const {
        data: logEventsData,
        isLoading: logEventsLoading,
        refetch: refetchLogEvents,
    } = useGetCloudWatchLogEventsQuery(
        {
            jwtToken: user?.jwtToken || '',
            logGroupName: selectedLogGroup || '',
            logStreamName: selectedLogStream || '',
            startTime: dateRange ? dateRange[0].valueOf() : undefined,
            endTime: dateRange ? dateRange[1].valueOf() : undefined,
            limit: 500,
            startFromHead: false,
        },
        { skip: !user?.jwtToken || !selectedLogGroup || !selectedLogStream }
    );

    // Update logs when log events are fetched
    useEffect(() => {
        if (logEventsData?.events) {
            setLogs(logEventsData.events);
        }
    }, [logEventsData]);

    // Auto-scroll to bottom when new logs arrive
    useEffect(() => {
        if (autoScroll && logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs, autoScroll]);

    // Start/stop tailing
    const handleToggleTailing = () => {
        if (isTailing) {
            stopTailing();
        } else {
            startTailing();
        }
    };

    const startTailing = () => {
        if (!selectedLogGroup || !user?.jwtToken) {
            message.warning('Please select a log group first');
            return;
        }

        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        const streamNames = selectedLogStream ? `&logStreamNames=${encodeURIComponent(selectedLogStream)}` : '';
        // EventSource doesn't support custom headers, so pass token as query parameter
        const url = `${apiBaseUrl}cloudwatch-logs/tail-logs/${encodeURIComponent(selectedLogGroup)}?token=${encodeURIComponent(user.jwtToken)}${streamNames}`;

        const eventSource = new EventSource(url);

        eventSource.onmessage = (event) => {
            try {
                const logEvent: LogEntry = JSON.parse(event.data);
                setLogs((prev) => [...prev, logEvent]);
            } catch (error) {
                console.error('Error parsing log event:', error);
            }
        };

        eventSource.onerror = (error) => {
            console.error('EventSource error:', error);
            message.error('Live tail connection lost');
            stopTailing();
        };

        eventSourceRef.current = eventSource;
        setIsTailing(true);
        message.success('Live tailing started');
    };

    const stopTailing = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setIsTailing(false);
        message.info('Live tailing stopped');
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopTailing();
        };
    }, []);

    const handleLogGroupChange = (value: string) => {
        setSelectedLogGroup(value);
        setSelectedLogStream(null);
        setLogs([]);
        stopTailing();
    };

    const handleLogStreamChange = (value: string) => {
        setSelectedLogStream(value);
        setLogs([]);
        stopTailing();
    };

    const handleClearLogs = () => {
        setLogs([]);
        message.success('Logs cleared');
    };

    const handleExportLogs = () => {
        const logsText = logs
            .map((log) => {
                const timestamp = new Date(log.timestamp).toISOString();
                return `${timestamp} ${log.message}`;
            })
            .join('\n');

        const blob = new Blob([logsText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cloudwatch-logs-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        message.success('Logs exported successfully');
    };

    const filteredLogs = logs.filter((log) =>
        filterText ? log.message.toLowerCase().includes(filterText.toLowerCase()) : true
    );

    const formatTimestamp = (timestamp: number): string => {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3,
        });
    };

    const getLogLevel = (message: string): { level: string; color: string } => {
        const upperMessage = message.toUpperCase();
        if (upperMessage.includes('ERROR')) return { level: 'ERROR', color: '#ff4d4f' };
        if (upperMessage.includes('WARN')) return { level: 'WARN', color: '#faad14' };
        if (upperMessage.includes('INFO')) return { level: 'INFO', color: '#1890ff' };
        if (upperMessage.includes('DEBUG')) return { level: 'DEBUG', color: '#8c8c8c' };
        return { level: 'LOG', color: '#595959' };
    };

    return (
        <div>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>
                            CloudWatch Logs Viewer
                        </Title>
                        <Text type="secondary" style={{ marginLeft: 16, fontSize: 14 }}>
                            View and tail AWS ECS application logs in real-time
                        </Text>
                    </div>
                }
                style={{ marginBottom: 24 }}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {/* Log Group and Stream Selectors */}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 250 }}>
                            <Text strong>Log Group:</Text>
                            <Select
                                showSearch
                                style={{ width: '100%', marginTop: 8 }}
                                placeholder="Select a log group"
                                value={selectedLogGroup}
                                onChange={handleLogGroupChange}
                                loading={logGroupsLoading}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={logGroups?.map((lg) => ({
                                    label: lg.logGroupName,
                                    value: lg.logGroupName,
                                }))}
                            />
                        </div>

                        <div style={{ flex: 1, minWidth: 250 }}>
                            <Text strong>Log Stream:</Text>
                            <Select
                                showSearch
                                style={{ width: '100%', marginTop: 8 }}
                                placeholder="Select a log stream"
                                value={selectedLogStream}
                                onChange={handleLogStreamChange}
                                loading={logStreamsLoading}
                                disabled={!selectedLogGroup}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={logStreams?.map((ls) => ({
                                    label: `${ls.logStreamName} ${ls.lastEventTimestamp ? `(${new Date(ls.lastEventTimestamp).toLocaleString()})` : ''}`,
                                    value: ls.logStreamName,
                                }))}
                            />
                        </div>
                    </div>

                    {/* Controls */}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Space>
                            <Tooltip title={isTailing ? 'Stop tailing' : 'Start tailing logs in real-time'}>
                                <Button
                                    icon={isTailing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                    onClick={handleToggleTailing}
                                    disabled={!selectedLogGroup}
                                    type={isTailing ? 'primary' : 'default'}
                                    danger={isTailing}
                                >
                                    {isTailing ? 'Stop Tail' : 'Start Tail'}
                                </Button>
                            </Tooltip>

                            <Tooltip title="Refresh logs">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => refetchLogEvents()}
                                    disabled={!selectedLogStream || isTailing}
                                    loading={logEventsLoading}
                                >
                                    Refresh
                                </Button>
                            </Tooltip>

                            <Tooltip title="Clear displayed logs">
                                <Button icon={<ClearOutlined />} onClick={handleClearLogs} disabled={logs.length === 0}>
                                    Clear
                                </Button>
                            </Tooltip>

                            <Tooltip title="Export logs to file">
                                <Button icon={<DownloadOutlined />} onClick={handleExportLogs} disabled={logs.length === 0}>
                                    Export
                                </Button>
                            </Tooltip>
                        </Space>

                        <div style={{ marginLeft: 'auto' }}>
                            <Space>
                                <Text>Auto-scroll:</Text>
                                <Switch checked={autoScroll} onChange={setAutoScroll} />
                            </Space>
                        </div>
                    </div>

                    {/* Filter and Date Range */}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <Input
                            style={{ flex: 1, minWidth: 200 }}
                            placeholder="Filter logs by text..."
                            prefix={<SearchOutlined />}
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            allowClear
                        />

                        <RangePicker
                            showTime
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
                            disabled={isTailing}
                            style={{ minWidth: 350 }}
                        />
                    </div>

                    {/* Status Info */}
                    {isTailing && (
                        <Alert
                            message="Live Tailing Active"
                            description="Streaming logs in real-time. Click 'Stop Tail' to pause."
                            type="info"
                            showIcon
                            closable
                        />
                    )}

                    {/* Logs Display */}
                    <Card
                        size="small"
                        style={{
                            backgroundColor: '#1e1e1e',
                            color: '#d4d4d4',
                            maxHeight: 600,
                            overflow: 'auto',
                        }}
                        styles={{ body: { padding: 16 } }}
                    >
                        {logEventsLoading && !isTailing ? (
                            <div style={{ textAlign: 'center', padding: 40 }}>
                                <Spin size="large" />
                            </div>
                        ) : filteredLogs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
                                <Text style={{ color: '#8c8c8c' }}>
                                    {selectedLogStream
                                        ? 'No logs found. Try adjusting the time range or filters.'
                                        : 'Select a log group and stream to view logs'}
                                </Text>
                            </div>
                        ) : (
                            <div style={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.8 }}>
                                {filteredLogs.map((log, index) => {
                                    const { level, color } = getLogLevel(log.message);
                                    return (
                                        <div
                                            key={`${log.timestamp}-${index}`}
                                            style={{
                                                borderBottom: '1px solid #333',
                                                padding: '8px 0',
                                                display: 'flex',
                                                gap: 12,
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    color: '#569cd6',
                                                    minWidth: 200,
                                                    fontFamily: 'monospace',
                                                }}
                                            >
                                                {formatTimestamp(log.timestamp)}
                                            </Text>
                                            <Tag color={color} style={{ margin: 0, minWidth: 60, textAlign: 'center' }}>
                                                {level}
                                            </Tag>
                                            <Text
                                                style={{
                                                    color: '#d4d4d4',
                                                    flex: 1,
                                                    wordBreak: 'break-word',
                                                    fontFamily: 'monospace',
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {log.message}
                                            </Text>
                                        </div>
                                    );
                                })}
                                <div ref={logsEndRef} />
                            </div>
                        )}
                    </Card>

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: 16, justifyContent: 'space-between' }}>
                        <Text type="secondary">
                            Showing {filteredLogs.length} of {logs.length} log entries
                        </Text>
                        {selectedLogGroup && (
                            <Text type="secondary" code>
                                {selectedLogGroup}
                                {selectedLogStream && ` / ${selectedLogStream}`}
                            </Text>
                        )}
                    </div>
                </Space>
            </Card>
        </div>
    );
};
